import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, ApiError, onUnauthorized } from '@/api/client';
import type { Me, MeEntity } from '@/api/types';
import { startActivityTracking } from './activity';
import { applyTheme } from './theme';

const ENTITY_KEY = 'suite.entity';
const ENTRY_KEY = 'suite.entry';
const SUPERADMIN_KEY = 'suite.superadmin';

/**
 * Marca de «acaba de ingresar» (en `sessionStorage`, sobrevive al regreso del proveedor federado): con ella el Inicio
 * lleva directo a la app si es la única de la persona. Solo al ingresar: volver a la suite desde la app no rebota.
 */
export const entry = {
  mark: () => { try { sessionStorage.setItem(ENTRY_KEY, '1'); } catch { /* modo privado */ } },
  peek: () => { try { return sessionStorage.getItem(ENTRY_KEY) === '1'; } catch { return false; } },
  clear: () => { try { sessionStorage.removeItem(ENTRY_KEY); } catch { /* modo privado */ } },
};

/**
 * Entidad activa que este navegador recuerda (la última elegida, o la de `?entidad=` con que llegó a la portada): al
 * ingresar es la entidad activa si la persona pertenece a ella, y al salir o vencer la sesión la suite la pone en la
 * URL de la portada. La portada no la lee: antes de ingresar la entidad siempre va explícita en la URL (`entityHint`).
 */
const SLUG = /^[a-z][a-z0-9-]{1,30}$/;
export const rememberedEntity = {
  get: (): string | null => { try { const s = localStorage.getItem(ENTITY_KEY); return s && SLUG.test(s) ? s : null; } catch { return null; } },
  set: (slug: string | null) => { try { if (slug && SLUG.test(slug)) localStorage.setItem(ENTITY_KEY, slug); else localStorage.removeItem(ENTITY_KEY); } catch { /* modo privado */ } },
};

/**
 * Si quien ingresó por última vez en este navegador es superadministrador. Solo decide a qué portada vuelve una app
 * que pide ingreso sin sesión (la de Datalog, la negra, en vez de la de la entidad): no da ningún permiso.
 */
export const lastSuperadmin = {
  get: (): boolean => { try { return localStorage.getItem(SUPERADMIN_KEY) === '1'; } catch { return false; } },
  set: (yes: boolean) => { try { if (yes) localStorage.setItem(SUPERADMIN_KEY, '1'); else localStorage.removeItem(SUPERADMIN_KEY); } catch { /* modo privado */ } },
};

interface Session {
  me: Me | null;
  loading: boolean;
  superadmin: boolean;
  /** Entidad activa en el portal (la última elegida, o la única). */
  entity: MeEntity | null;
  setEntity: (slug: string) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const Ctx = createContext<Session>({ me: null, loading: true, superadmin: false, entity: null, setEntity: () => undefined, logout: async () => undefined, refresh: async () => undefined });

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await apiGet<Me>('/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  const me = q.data ?? null;
  // Sin sesión previa no hay nada privado que borrar: el 401 de `/me` al abrir una pantalla pública (la interacción
  // OIDC, el ingreso) no debe llevarse las consultas públicas que ya llegaron.
  const end = useCallback(() => {
    const had = qc.getQueryData(['me']);
    qc.setQueryData(['me'], null);
    if (had) qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
  }, [qc]);
  useEffect(() => startActivityTracking(), []);
  useEffect(() => onUnauthorized(() => end()), [end]);
  useEffect(() => {
    if (me?.preferences.theme) applyTheme(me.preferences.theme);
  }, [me?.preferences.theme]);
  useEffect(() => { if (me) lastSuperadmin.set(me.platformRole === 'SUPERADMIN'); }, [me]);
  const entity = useMemo(() => {
    if (!me?.entities.length) return null;
    let wanted: string | null = null;
    try {
      wanted = localStorage.getItem(ENTITY_KEY);
    } catch {
      /* modo privado */
    }
    return me.entities.find((e) => e.slug === wanted) ?? me.entities[0]!;
  }, [me]);
  const value: Session = {
    me,
    loading: q.isLoading,
    superadmin: me?.platformRole === 'SUPERADMIN',
    entity,
    setEntity: (slug) => {
      try {
        localStorage.setItem(ENTITY_KEY, slug);
      } catch {
        /* modo privado */
      }
      void q.refetch();
    },
    logout: async () => {
      await apiPost('/auth/logout').catch(() => undefined);
      end();
    },
    refresh: () => q.refetch(),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSession = () => useContext(Ctx);
